import {
	Activity,
	BellRing,
	CalendarDays,
	CheckCircle2,
	CircleDot,
	Flag,
	ListChecks,
	Plane,
	Target,
	User,
	Users
} from 'lucide-react';

// Mirrors the CRM calendar (nitex-crm-app calendar-dates.ts / calendar-meta.ts)
// so events read the same in both apps.

const MAX_DAY_SPAN = 62;

export const EVENT_TYPES = {
	preselection_meeting: { label: 'Pre-selection meeting', icon: ListChecks, order: 1 },
	selection_meeting: { label: 'Selection meeting', icon: Users, order: 2 },
	buyer_visit: { label: 'Buyer visit', icon: Plane, order: 3 },
	general: { label: 'General', icon: CalendarDays, order: 4 },
	reminder: { label: 'Reminder', icon: BellRing, order: 4.5 },
	personal: { label: 'Personal (Outlook)', icon: User, order: 5 },
	project_created: { label: 'Project created', icon: CircleDot, order: 6 },
	expected_close: { label: 'Expected close', icon: Target, order: 7 },
	task_due: { label: 'Task due', icon: CheckCircle2, order: 8 },
	activity: { label: 'Activity', icon: Activity, order: 9 },
	stage_change: { label: 'Stage change', icon: Flag, order: 10 }
};

export const eventTypeMeta = (event) => EVENT_TYPES[event.eventType] || EVENT_TYPES.general;

export const VISIT_LOCATION_LABEL = { UK: 'UK', BD: 'Bangladesh', ES: 'Spain', ONLINE: 'Online' };

/** "Invited by X" for events someone else set up; null for the user's own Outlook events. */
export const organizerLabel = (event) => {
	const organizer = event.organizer;
	if (!organizer || (event.source === 'personal' && organizer.isYou)) return null;
	const who = organizer.isYou ? 'you' : (organizer.name ?? organizer.email);
	if (!who) return null;
	if (organizer.via === 'mail') return organizer.isYou ? 'From your email' : `From ${who}'s email`;
	return `${event.eventType === 'reminder' ? 'Created' : 'Invited'} by ${who}`;
};

const URL_PATTERN = /\b(?:https?:\/\/|www\.)\S+/gi;

/**
 * Where the meeting happens, without join links: Outlook often puts the Teams,
 * Zoom or Meet URL in the location. Buyer visits fall back to their visit
 * country. Null when nothing but a link was there.
 */
export const meetingPlace = (event) => {
	const place = (event.location ?? '')
		.replace(URL_PATTERN, '')
		.split(/[;\n]/)
		.map((part) => part.replace(/^[\s,|·-]+|[\s,|·-]+$/g, ''))
		.filter(Boolean)
		.join('; ');
	if (place) return place;
	return event.eventType === 'buyer_visit' && event.visitLocation
		? VISIT_LOCATION_LABEL[event.visitLocation]
		: null;
};

export const dateKey = (date) => {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
};

export const parseDateKey = (key) => {
	const [year, month, day] = key.split('-').map(Number);
	return new Date(year, month - 1, day);
};

export const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date, days) => {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
};

export const browserTimeZone = () => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	} catch {
		return 'UTC';
	}
};

export const eventStart = (event) =>
	event.isAllDay && event.startDate ? parseDateKey(event.startDate) : new Date(event.startAt);

/** Exclusive end; null for point-in-time project dates. */
export const eventEnd = (event) => {
	if (event.isAllDay && event.endDate) return parseDateKey(event.endDate);
	return event.endAt ? new Date(event.endAt) : null;
};

/** Every local day an event covers, so multi-day buyer visits show on each day. */
export const eventDayKeys = (event) => {
	const start = eventStart(event);
	const end = eventEnd(event);
	if (!end || end.getTime() <= start.getTime()) return [dateKey(start)];
	const keys = [];
	const last = startOfDay(new Date(end.getTime() - 1));
	for (
		let cursor = startOfDay(start);
		cursor.getTime() <= last.getTime() && keys.length < MAX_DAY_SPAN;
		cursor = addDays(cursor, 1)
	) {
		keys.push(dateKey(cursor));
	}
	return keys;
};

const formatTime = (date) => date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const formatDay = (date) =>
	date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export const sortEvents = (events) =>
	[...events].sort((a, b) => {
		if (a.isAllDay !== b.isAllDay) return a.isAllDay ? -1 : 1;
		const diff = eventStart(a).getTime() - eventStart(b).getTime();
		if (diff !== 0) return diff;
		const orderDiff = eventTypeMeta(a).order - eventTypeMeta(b).order;
		if (orderDiff !== 0) return orderDiff;
		return a.title.localeCompare(b.title);
	});

// --- Meetings ---------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

const MEETING_TYPES = ['buyer_visit', 'preselection_meeting', 'selection_meeting'];

/** The drawer is a meetings list: buyer visits and (pre-)selection meetings, in date order. */
export const meetingEvents = (events) =>
	sortEvents(events.filter((event) => MEETING_TYPES.includes(event.eventType))).sort(
		(a, b) => eventStart(a).getTime() - eventStart(b).getTime()
	);

/** Whole local days from today to the meeting's first day; negative once it has started. */
export const daysUntil = (event, today) =>
	Math.round((startOfDay(eventStart(event)).getTime() - today.getTime()) / DAY_MS);

export const dueLabel = (days) => {
	if (days < 0) return 'Ongoing';
	if (days === 0) return 'Due today';
	if (days === 1) return 'Due tomorrow';
	return `Due in ${days} days`;
};

/** The meeting's date, with its hours unless it runs all day. */
export const meetingDateLabel = (event) => {
	const start = eventStart(event);
	const end = eventEnd(event);
	// The end is exclusive, so step back a millisecond to land on the last day.
	const lastDay = end && end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : start;
	const multiDay = eventDayKeys(event).length > 1;
	if (event.isAllDay) return multiDay ? `${formatDay(start)} – ${formatDay(lastDay)}` : formatDay(start);
	if (!end) return `${formatDay(start)}, ${formatTime(start)}`;
	if (multiDay) return `${formatDay(start)}, ${formatTime(start)} – ${formatDay(end)}, ${formatTime(end)}`;
	return `${formatDay(start)}, ${formatTime(start)} – ${formatTime(end)}`;
};

/** "5 meetings in next 10 days": the count, and how far ahead the last one starts. */
export const meetingsSummary = (events, today) => {
	if (events.length === 0) return 'No upcoming meetings';
	const count = `${events.length} meeting${events.length === 1 ? '' : 's'}`;
	const span = Math.max(...events.map((event) => daysUntil(event, today)));
	if (span <= 0) return `${count} today`;
	return `${count} in next ${span} day${span === 1 ? '' : 's'}`;
};

// --- Mail -------------------------------------------------------------------

export const formatMailTimestamp = (value) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const now = new Date();
	if (dateKey(date) === dateKey(now)) {
		return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	}
	if (date.getFullYear() === now.getFullYear()) {
		return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
	}
	return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
};

export const counterpartyLabel = (thread) => {
	const wanted = thread.direction === 'outbound' ? 'to' : 'from';
	const match =
		thread.participants.find((participant) => participant.kind === wanted) ?? thread.participants[0];
	if (!match) return '(unknown)';
	return match.displayName?.trim() || match.emailAddress;
};
